/**
 * IA API Layer
 * Handles all API calls for AI-powered ElementsAppreciationSituation generation
 */

import { apiClient } from '@/core/api/client'
import type {
  ApiResponse,
  GetAppreciationResponse,
  GetActiveAffairesResponse,
  GenerationRequest,
  GenerationResponse,
  SaveAppreciationRequest,
  GetPromptsResponse,
  GetActivePromptResponse,
  CreatePromptRequest,
  GetHistoryResponse,
  SearchAffaireResponse,
  GetPendingApprovalsResponse,
  ApproveGenerationRequest
} from '../types/ia.types'

/**
 * IA API methods
 */
export const iaApi = {
  /**
   * Search for an affaire by ID or address (reusing existing search endpoint)
   * GET /api/admin/affaire/search
   */
  async searchAffaire(query: string): Promise<ApiResponse<SearchAffaireResponse>> {
    try {
      const params = new URLSearchParams()
      params.append('affaireId', query)

      const response = await apiClient.get(`/api/admin/affaire/search?${params}`)
      return response as ApiResponse<SearchAffaireResponse>
    } catch (error: any) {
      console.error('Error searching affaire:', error)
      throw new Error(error.response?.data?.message || 'Erreur lors de la recherche de l\'affaire')
    }
  },
  /**
   * Get current ElementsAppreciationSituation from fm_biens for a given affaire
   * GET /api/admin/ia/affaire/:affaireId/appreciation
   */
  async getAppreciation(affaireId: number): Promise<ApiResponse<GetAppreciationResponse>> {
    try {
      const response = await apiClient.get(`/api/admin/ia/affaire/${affaireId}/appreciation`)
      return response as ApiResponse<GetAppreciationResponse>
    } catch (error: any) {
      console.error('Error getting appreciation:', error)
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération de l\'appréciation')
    }
  },

  /**
   * Get list of active affaires (chat activity in last 7 days) with no appreciation yet
   * GET /api/admin/ia/active-affaires
   */
  async getActiveAffaires(): Promise<ApiResponse<GetActiveAffairesResponse>> {
    try {
      const response = await apiClient.get('/api/admin/ia/active-affaires')
      return response as ApiResponse<GetActiveAffairesResponse>
    } catch (error: any) {
      console.error('Error getting active affaires:', error)
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des affaires actives')
    }
  },

  /**
   * Get 100 most recent affaires without appreciation (based on fm_biens.updated_at)
   * GET /api/admin/ia/recent-affaires-without-appreciation
   */
  async getRecentAffairesWithoutAppreciation(): Promise<ApiResponse<GetActiveAffairesResponse>> {
    try {
      const response = await apiClient.get('/api/admin/ia/recent-affaires-without-appreciation')
      return response as ApiResponse<GetActiveAffairesResponse>
    } catch (error: any) {
      console.error('Error getting recent affaires without appreciation:', error)
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des affaires récentes')
    }
  },

  /**
   * Generate appreciation using GPT-5
   * POST /api/admin/ia/generate
   * Uses extended timeout (2 minutes) for AI processing
   */
  async generateAppreciation(request: GenerationRequest): Promise<ApiResponse<GenerationResponse>> {
    try {
      // Use extended timeout for AI generation (2 minutes instead of 30 seconds)
      const response = await apiClient.post('/api/admin/ia/generate', request, {
        timeout: 120000 // 2 minutes
      })
      return response as ApiResponse<GenerationResponse>
    } catch (error: any) {
      console.error('Error generating appreciation:', error)
      throw new Error(error.response?.data?.message || 'Erreur lors de la génération de l\'appréciation')
    }
  },

  /**
   * Save generated appreciation to fm_biens
   * POST /api/admin/ia/save-appreciation
   */
  async saveAppreciation(request: SaveAppreciationRequest): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.post('/api/admin/ia/save-appreciation', request)
      return response as ApiResponse<void>
    } catch (error: any) {
      console.error('Error saving appreciation:', error)
      throw new Error(error.response?.data?.message || 'Erreur lors de la sauvegarde de l\'appréciation')
    }
  },

  /**
   * Get all prompt versions
   * GET /api/admin/ia/prompts
   */
  async getPrompts(): Promise<ApiResponse<GetPromptsResponse>> {
    try {
      const response = await apiClient.get('/api/admin/ia/prompts')
      return response as ApiResponse<GetPromptsResponse>
    } catch (error: any) {
      console.error('Error getting prompts:', error)
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des prompts')
    }
  },

  /**
   * Get current active prompt
   * GET /api/admin/ia/prompts/active
   */
  async getActivePrompt(): Promise<ApiResponse<GetActivePromptResponse>> {
    try {
      const response = await apiClient.get('/api/admin/ia/prompts/active')
      return response as ApiResponse<GetActivePromptResponse>
    } catch (error: any) {
      console.error('Error getting active prompt:', error)
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération du prompt actif')
    }
  },

  /**
   * Create new prompt version (deactivates previous)
   * POST /api/admin/ia/prompts
   */
  async createPrompt(request: CreatePromptRequest): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.post('/api/admin/ia/prompts', request)
      return response as ApiResponse<void>
    } catch (error: any) {
      console.error('Error creating prompt:', error)
      throw new Error(error.response?.data?.message || 'Erreur lors de la création du prompt')
    }
  },

  /**
   * Activate a specific prompt version
   * PUT /api/admin/ia/prompts/:id/activate
   */
  async activatePrompt(promptId: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.put(`/api/admin/ia/prompts/${promptId}/activate`)
      return response as ApiResponse<void>
    } catch (error: any) {
      console.error('Error activating prompt:', error)
      throw new Error(error.response?.data?.message || 'Erreur lors de l\'activation du prompt')
    }
  },

  /**
   * Get generation history for an affaire
   * GET /api/admin/ia/history/:affaireId
   */
  async getHistory(affaireId: number): Promise<ApiResponse<GetHistoryResponse>> {
    try {
      const response = await apiClient.get(`/api/admin/ia/history/${affaireId}`)
      return response as ApiResponse<GetHistoryResponse>
    } catch (error: any) {
      console.error('Error getting history:', error)
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération de l\'historique')
    }
  },

  /**
   * Delete a prompt (cannot delete active prompt)
   * DELETE /api/admin/ia/prompts/:id
   */
  async deletePrompt(promptId: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.delete(`/api/admin/ia/prompts/${promptId}`)
      return response as ApiResponse<void>
    } catch (error: any) {
      console.error('Error deleting prompt:', error)
      throw new Error(error.response?.data?.message || 'Erreur lors de la suppression du prompt')
    }
  },

  /**
   * Get all pending approval generations with affaire details
   * GET /api/admin/ia/pending-approvals
   */
  async getPendingApprovals(): Promise<ApiResponse<GetPendingApprovalsResponse>> {
    try {
      const response = await apiClient.get('/api/admin/ia/pending-approvals')
      return response as ApiResponse<GetPendingApprovalsResponse>
    } catch (error: any) {
      console.error('Error getting pending approvals:', error)
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des approbations en attente')
    }
  },

  /**
   * Approve a generated text and save to fm_biens
   * POST /api/admin/ia/approve/:historyId
   */
  async approveGeneration(historyId: string, request: ApproveGenerationRequest): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.post(`/api/admin/ia/approve/${historyId}`, request)
      return response as ApiResponse<void>
    } catch (error: any) {
      console.error('Error approving generation:', error)
      throw new Error(error.response?.data?.message || 'Erreur lors de l\'approbation de la génération')
    }
  },

  /**
   * Reject a generated text
   * POST /api/admin/ia/reject/:historyId
   */
  async rejectGeneration(historyId: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.post(`/api/admin/ia/reject/${historyId}`)
      return response as ApiResponse<void>
    } catch (error: any) {
      console.error('Error rejecting generation:', error)
      throw new Error(error.response?.data?.message || 'Erreur lors du rejet de la génération')
    }
  }
}
